import { zodResolver } from "@hookform/resolvers/zod";
import {
  KikMarketingCloudDoubleOptInProcedures,
  KikPasswordResetSendEmailVerificationFields,
  kikPasswordResetSendEmailVerificationFieldsSchema,
  KikUserZodSchemas,
} from "@kikocosmeticsorg/uc-api-nest-common-fe";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import z from "zod";

import Button from "~/components/common/button";
import richTextStyle from "~/components/common/rich-text.module.scss";
import Errors from "~/components/form/errors";
import InputField from "~/components/form/input-field";
import { useActionCodeContext } from "~/contexts/action-code";
import { useAuthContext } from "~/contexts/auth";
import { useFormTranslations } from "~/hooks/use-form-translations";
import Logger from "~/services/logger/logger";
import styles from "~/styles/pages/password-reset-request.module.scss";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";

const formSchema = kikPasswordResetSendEmailVerificationFieldsSchema.pick({
  EmailAddress: !0,
});

type FormData = z.infer<typeof formSchema>;

export default function PasswordResetStep1({ onNext }: { onNext: (email: string) => void }) {
  const t = useTranslations();
  const router = useRouter();
  const { timeLeft, resetTimer } = useActionCodeContext();
  const { sendEmailVerification, isLoading, authError } = useAuthContext();
  const currentCountry = crossCountryUtils.getCurrentCountryCode(router);
  const currentLanguage = crossCountryUtils.getCurrentLanguageCode(router);

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
    getValues,
  } = useForm<KikPasswordResetSendEmailVerificationFields>({
    ...Constants.USE_FORM_VALIDATION_DEFAULTS,
    resolver: zodResolver(formSchema),
  });
  const { getFieldError } = useFormTranslations();

  const doSendEmailVerification = (data: FormData = getValues()) => {
    sendEmailVerification({
      ...data,
      CountryID: currentCountry,
      LanguageID: currentLanguage,
      procedure: KikMarketingCloudDoubleOptInProcedures.PASSWORD_RESET,
    }).then(() => {
      resetTimer();
      Logger.instance.info("PasswordResetStep1: sent email");
      onNext(data.EmailAddress);
    });
  };

  const handleAlreadyHaveCode = () => {
    onNext(getValues("EmailAddress"));
  };

  return (
    <form
      onSubmit={handleSubmit(doSendEmailVerification, (data) =>
        Logger.instance.warn("PasswordResetStep1: invalid form", data)
      )}
      className={styles.formContainer}
    >
      <p className={richTextStyle.paragraph}>{t("generic.passwordResetStep1Description")}</p>
      <InputField
        {...register("EmailAddress")}
        type="email"
        label={t("generic.form.email_label")}
        error={getFieldError(errors.EmailAddress?.message, {
          fieldName: t("generic.form.email_label"),
          minLength: KikUserZodSchemas.emailMaxlength,
        })}
        onClearErrors={() => clearErrors("EmailAddress")}
      />
      <Errors error={authError}></Errors>
      <span className={styles.mandatory}>{t("generic.form.mandatory_long")}</span>
      {timeLeft ? (
        <p className={richTextStyle.paragraph}>
          {t.rich("generic.signUpDoubleOptInSendAnotherIn", {
            seconds: timeLeft,
          })}
        </p>
      ) : null}
      <div className={classNames(styles.buttonsWrapper, styles.vertical)}>
        <Button
          type="submit"
          variant="primary"
          className={styles.button}
          disabled={(timeLeft || 0) > 0}
          loading={isLoading || !router.isReady}
        >
          {t("generic.send_request")}
        </Button>
        {!!timeLeft && (
          <Button
            className={styles.button}
            variant="secondary"
            onClick={() => handleAlreadyHaveCode()}
            type="button"
            disabled={!getValues("EmailAddress")}
          >
            {t("generic.myaccount.settings.iHaveACode")}
          </Button>
        )}
      </div>
    </form>
  );
}
